/**
 * <ImportKPICards /> — 3 cards (Valid / Attention / Blocking)
 * inside ImportDashboard.
 *
 * Per BUILD_READINESS_M29.md acceptance criterion 14:
 *   Green card  — "✓ N rows valid" + optional %
 *   Amber card  — "⚠ N need attention" + breakdown (e.g.
 *                 "47 duplicates, 89 format issues, 17 outliers")
 *   Red card    — "✗ N blocking errors" (only visible when N > 0)
 *
 * Each card has the brand-gradient top strip per the design
 * fidelity sprint. Numbers animate in (count-up) per acceptance
 * criterion 16. Respects prefers-reduced-motion via framer-motion's
 * built-in handling on `animate` props.
 *
 * Tiny + dense — these live INSIDE the 120px dashboard, not as a
 * standalone hero. The KPICard from components/ui/ is too large.
 */

import { AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { DURATION, EASE, withReducedMotion } from "../../lib/motion";

export interface ImportKPICardsProps {
  valid: number;
  attention: number;
  blocking: number;
  /** Optional breakdown shown under the Attention number. */
  attentionBreakdown?: string | null;
  /** When provided, shows percentages alongside counts. */
  total?: number | null;
}

export function ImportKPICards({
  valid,
  attention,
  blocking,
  attentionBreakdown,
  total,
}: ImportKPICardsProps) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" aria-live="polite">
      <_MiniCard
        tone="green"
        Icon={CheckCircle2}
        label={t("app.ikc_valid")}
        count={valid}
        total={total}
      />
      <_MiniCard
        tone="amber"
        Icon={AlertTriangle}
        label={t("app.ikc_attention")}
        count={attention}
        total={total}
        subtitle={attentionBreakdown}
      />
      {blocking > 0 && (
        <_MiniCard
          tone="rose"
          Icon={AlertCircle}
          label={t("app.ikc_blocking")}
          count={blocking}
          total={total}
        />
      )}
    </div>
  );
}

interface MiniCardProps {
  tone: "green" | "amber" | "rose";
  Icon: typeof CheckCircle2;
  label: string;
  count: number;
  total?: number | null;
  subtitle?: string | null;
}

function _MiniCard({
  tone,
  Icon,
  label,
  count,
  total,
  subtitle,
}: MiniCardProps) {
  const cls = {
    green: {
      card: "border-emerald-200 bg-emerald-50",
      icon: "text-emerald-700",
      value: "text-emerald-900",
      stripe: "bg-emerald-500",
    },
    amber: {
      card: "border-amber-200 bg-amber-50",
      icon: "text-amber-700",
      value: "text-amber-900",
      stripe: "bg-amber-500",
    },
    rose: {
      card: "border-rose-200 bg-rose-50",
      icon: "text-rose-700",
      value: "text-rose-900",
      stripe: "bg-rose-500",
    },
  }[tone];

  const pct = total && total > 0 ? Math.round((count / total) * 100) : null;

  return (
    <div
      className={`relative overflow-hidden rounded-md border p-2 ${cls.card}`}
    >
      <div
        className={`absolute inset-x-0 top-0 h-[2px] ${cls.stripe}`}
        aria-hidden="true"
      />
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Icon size={12} className={cls.icon} aria-hidden="true" />
          <span className="text-[10px] uppercase tracking-wider text-text-secondary">
            {label}
          </span>
        </div>
        {pct !== null && (
          <span className="text-[10px] text-text-tertiary">{pct}%</span>
        )}
      </div>
      <motion.div
        key={count}
        initial={{ opacity: 0, y: 2 }}
        animate={{ opacity: 1, y: 0 }}
        transition={withReducedMotion({
          duration: DURATION.micro / 1000,
          ease: EASE.out,
        })}
        className={`mt-1 text-lg font-semibold tabular-nums ${cls.value}`}
      >
        {count.toLocaleString()}
      </motion.div>
      {subtitle && (
        <div className="mt-0.5 truncate text-[10px] text-text-tertiary">
          {subtitle}
        </div>
      )}
    </div>
  );
}
